'use client';

import React, { useState } from 'react';
import {
  CheckCircleIcon,
  ChevronDownIcon,
  ClockIcon,
  Loader2Icon,
  XCircleIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { HighlightedJson } from './HighlightedJson.js';
import { getToolLabel } from '../tool-display/tool-labels.js';
import { getResultSummary } from '../tool-display/result-summary.js';
import {
  formatDuration,
  isTerminalStatus,
  resolveToolStatus,
  trackToolTiming,
  type ToolStatus,
} from '../tool-display/tool-status.js';
import { renderToolResultCard } from '../tool-display/tool-result-cards.js';
import type { ToolPartWithState } from '../utils/toolInvocations.js';
import { resolveToolName } from '../utils/messageTimeline.js';

/** Status icon per resolved status — the live spinner is the running state. */
function getStatusIcon(status: ToolStatus): ReactNode {
  switch (status) {
    case 'pending':
    case 'running':
    case 'responded':
      return <Loader2Icon className="size-3.5 animate-spin text-primary" aria-label="running" />;
    case 'approval':
      return <ClockIcon className="size-3.5 text-yellow-600 dark:text-yellow-500" aria-label="awaiting approval" />;
    case 'done':
      return <CheckCircleIcon className="size-3.5 text-green-600 dark:text-green-500" aria-label="completed" />;
    case 'error':
      return <XCircleIcon className="size-3.5 text-destructive" aria-label="error" />;
    case 'denied':
      return <XCircleIcon className="size-3.5 text-orange-600 dark:text-orange-500" aria-label="denied" />;
  }
}

const STATUS_SUFFIX: Partial<Record<ToolStatus, string>> = {
  approval: 'awaiting approval',
  denied: 'denied',
};

/** Pretty-print an already-serializable tool payload. */
function formatJson(value: unknown): string {
  const serialized = JSON.stringify(value, null, 2);
  return serialized === undefined ? String(value) : serialized;
}

export interface ToolBlockProps {
  part: ToolPartWithState;
  pairedResult?: ToolPartWithState;
  defaultOpen?: boolean;
  className?: string;
  /**
   * Flat row for use inside a TurnActivityBlock (no own border/rounding —
   * the group supplies the frame).
   */
  variant?: 'card' | 'row';
}

/**
 * ToolBlock — one tool invocation, narrated.
 *
 * - Header: status icon (live spinner while running) + verb-phrase label
 *   ("Searching contacts · ACME") + one-line result summary + duration.
 * - Expanded: typed result card for known CRM tools when the shape matches,
 *   otherwise readable JSON; raw parameters/result JSON always available
 *   behind a "raw JSON" toggle when a card is shown (three-level disclosure).
 * - Errors auto-open and keep the failure reason visible even when closed.
 */
export function ToolBlock({
  part,
  pairedResult,
  defaultOpen = false,
  className = '',
  variant = 'card',
}: ToolBlockProps) {
  // Shared resolver: persisted parts are typed `tool-call` with the real name
  // on `toolName`, streaming parts encode it as `tool-<name>`.
  const toolName = resolveToolName(part) || 'Tool';
  const status = resolveToolStatus(part, pairedResult);
  const isRunning = !isTerminalStatus(status) && status !== 'approval';
  const durationMs = trackToolTiming(part.toolCallId ?? '', status);

  // Determine what to display. Persisted rows carry output/errorText on the
  // call part itself (no separate result part), so read both places.
  const inputValue = part.input !== undefined ? part.input : part.args;
  const hasInput = inputValue !== undefined;
  const output = pairedResult?.output !== undefined ? pairedResult.output : part.output;
  const hasOutput = output !== undefined;
  const errorText = pairedResult?.errorText ?? part.errorText;
  const hasError = errorText !== undefined;
  // TOON / plain-text results arrive as strings — render them verbatim in a
  // monospace block (never JSON.stringify a string: quoted + \n-escaped soup).
  const isTextOutput = typeof output === 'string';
  const resultSummary = getResultSummary(output, errorText);

  const label = getToolLabel(toolName, inputValue);
  const labelText = status === 'done' || status === 'error' || status === 'denied' ? label.done : label.running;
  const statusSuffix = STATUS_SUFFIX[status];

  // Typed card for known CRM tools; null → generic JSON detail.
  const resultCard = hasError ? null : renderToolResultCard(toolName, output);
  const [showRaw, setShowRaw] = useState(false);

  // A failed tool must never hide its reason behind a collapsed disclosure on
  // an older message — start open when there's an error so the failure is visible.
  const [isOpen, setIsOpen] = useState(defaultOpen || hasError);

  const frameClass =
    variant === 'card'
      ? `mt-3 rounded-lg border ${hasError ? 'border-destructive/40' : 'border-border'} overflow-hidden`
      : 'overflow-hidden';

  return (
    <div className={`${frameClass} ${className}`}>
      {/* Header / Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full min-h-9 flex items-center gap-2 px-3 py-2 hover:bg-muted/50 transition-colors text-left"
        aria-expanded={isOpen}
      >
        <span className="flex-shrink-0">{getStatusIcon(status)}</span>
        <span
          className={`text-sm min-w-0 truncate ${isRunning ? 'text-foreground' : ''}`}
          title={toolName}
        >
          <span className="font-medium">{labelText}</span>
          {label.subject && <span className="text-muted-foreground"> · {label.subject}</span>}
          {statusSuffix && <span className="text-muted-foreground"> — {statusSuffix}</span>}
        </span>
        {!isOpen && !hasError && resultSummary && (
          <span className="text-xs text-muted-foreground truncate min-w-0">
            · {resultSummary}
          </span>
        )}
        <span className="ml-auto flex items-center gap-2 flex-shrink-0">
          {durationMs !== null && (
            <span className="text-xs text-muted-foreground tabular-nums">
              {formatDuration(durationMs)}
            </span>
          )}
          <ChevronDownIcon
            className={`size-4 text-muted-foreground transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </span>
      </button>

      {/* Collapsed-state error summary — keep the failure reason visible even
          if the user closes the block. */}
      {!isOpen && hasError && (
        <div className="px-3 pb-2 -mt-1 text-xs text-destructive break-words">{errorText}</div>
      )}

      {/* Content */}
      {isOpen && (
        <div className="border-t border-border/60 space-y-3 px-3 py-3">
          {/* Typed result card (level 2) */}
          {resultCard}

          {/* Error detail */}
          {hasError && (
            <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive whitespace-pre-wrap break-words">
              {errorText}
            </div>
          )}

          {/* Raw detail (level 3 when a card is shown, level 2 otherwise) */}
          {resultCard ? (
            <div>
              <button
                type="button"
                onClick={() => setShowRaw(!showRaw)}
                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
              >
                {showRaw ? 'Hide raw JSON' : 'View raw JSON'}
              </button>
            </div>
          ) : null}

          {(!resultCard || showRaw) && (
            <>
              {/* Parameters section */}
              {hasInput && (
                <div className="space-y-2">
                  <h4 className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                    Parameters
                  </h4>
                  <div className="meridian-hljs rounded-md bg-muted/50 p-2 overflow-x-auto">
                    <pre className="text-xs text-foreground whitespace-pre">
                      <HighlightedJson code={formatJson(inputValue)} />
                    </pre>
                  </div>
                </div>
              )}

              {/* Result section */}
              {hasOutput && !hasError && (
                <div className="space-y-2">
                  <h4 className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                    Result
                  </h4>
                  <div className="meridian-hljs rounded-md bg-muted/50 text-foreground p-2 overflow-x-auto">
                    {isTextOutput ? (
                      // TOON / plain-text table result: verbatim monospace, no
                      // wrapping so column alignment survives; container scrolls.
                      <pre className="text-xs font-mono whitespace-pre">{output}</pre>
                    ) : (
                      <pre className="text-xs whitespace-pre">
                        <HighlightedJson code={formatJson(output)} />
                      </pre>
                    )}
                  </div>
                </div>
              )}

              {/* No content message */}
              {!hasInput && !hasOutput && !hasError && (
                <p className="text-xs text-muted-foreground italic">
                  No parameters or results available
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
